import { getFeatureValue } from '@core/State/FeatureFlags/helpers/getFeatureValue'

type LDMigrationData = Record<
  string, // the from prefix
  {
    toPrefix: string
    rolloutPercentage: number
  }
>

/**
 * Helper function for the API migration
 *
 * Routes API traffic to a migrated endpoint based on rollout percentages.
 *
 * This function checks if the provided URL matches any migration stage prefix.
 * If a match is found, it randomly decides, based on the configured rollout percentage,
 * whether to reroute the request to the new API. If rerouting is chosen, the URL's
 * prefix is replaced with the target prefix; otherwise, the original URL is returned.
 *
 * @param url - The API endpoint URL to potentially reroute.
 * @returns The rerouted URL, or the original URL if no migration applies.
 */
export function routeApiMigrationTraffic(url: string): string {
  try {
    const migrationStages = getFeatureValue('api-migration-stages') as LDMigrationData

    const normalisedUrl = url.toLowerCase()

    const fromPrefix = Object.keys(migrationStages)
      .toSorted((a, b) => b.length - a.length)
      .find(route => normalisedUrl.startsWith(route.toLowerCase()))

    // Randomly reroute the request based on the rollout %
    if (fromPrefix && Math.random() <= migrationStages[fromPrefix].rolloutPercentage) {
      const toPrefix = migrationStages[fromPrefix].toPrefix
      return normalisedUrl.replace(fromPrefix.toLowerCase(), toPrefix)
    }
  } catch {
    /* swallow */
  }

  return url
}
